import { useEffect, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = {
  purchase_order_no: "", purchase_order_date: "", reg_serial_no: "", reg_date: "",
  supplier_name: "", invoice_number: "", invoice_date: "",
  total_quantity: "", total_shipper: "", total_piece: "", total_box: "", dcet_no: "",
  transporter_name: "", transporter_builty_no: "", material_receiver_name: "", remark: "",
};

export default function Purchase() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const { nameFor } = useUserNames();

  const load = async () => { const { data } = await client.get("/purchases"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditId(r.id); setOpen(true); };
  const save = async () => {
    try {
      const p = {
        ...form,
        total_quantity: Number(form.total_quantity),
        total_shipper: Number(form.total_shipper),
        total_piece: Number(form.total_piece || 0),
        total_box: Number(form.total_box || 0),
      };
      if (editId) await client.put(`/purchases/${editId}`, p); else await client.post("/purchases", p);
      toast.success(editId ? "Updated" : "Purchase recorded"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this purchase?")) return;
    try { await client.delete(`/purchases/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const F = (label, key, type = "text") => (
    <div>
      <label className="label-cap">{label}</label>
      <Input data-testid={`purchase-${key}-input`} type={type} value={form[key]} onChange={(e)=>setForm({...form, [key]: e.target.value})}/>
    </div>
  );

  return (
    <div>
      <PageHeader title="Purchase" subtitle="Inward stock from suppliers"
        actions={<Button data-testid="purchase-new-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} className="mr-1"/> New Purchase</Button>}
      />
      <Section>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">PO No</th><th className="px-4 py-3">PO Date</th>
              <th className="px-4 py-3">Reg Serial</th><th className="px-4 py-3">Supplier</th>
              <th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Inv Date</th>
              <th className="px-4 py-3">Qty</th><th className="px-4 py-3">Shipper</th>
              <th className="px-4 py-3">Piece</th><th className="px-4 py-3">Box</th>
              <th className="px-4 py-3">DCET</th>
              <th className="px-4 py-3">PT / SHOPPER</th>
              <th className="px-4 py-3">PT in QB</th>
              <th className="px-4 py-3">Transporter</th>
              <th className="px-4 py-3">Receiver</th><th className="px-4 py-3">Entered By</th>
              <th className="px-4 py-3">Date</th>{isAdmin && <th className="px-4 py-3">Actions</th>}
            </tr></thead>
            <tbody data-testid="purchases-tbody">
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono">{r.purchase_order_no}</td>
                  <td className="px-4 py-2 font-mono">{r.purchase_order_date}</td>
                  <td className="px-4 py-2 font-mono">{r.reg_serial_no}</td>
                  <td className="px-4 py-2">{r.supplier_name}</td>
                  <td className="px-4 py-2 font-mono">{r.invoice_number}</td>
                  <td className="px-4 py-2 font-mono">{r.invoice_date}</td>
                  <td className="px-4 py-2 font-mono">{r.total_quantity}</td>
                  <td className="px-4 py-2 font-mono">{r.total_shipper}</td>
                  <td className="px-4 py-2 font-mono">{r.total_piece || "—"}</td>
                  <td className="px-4 py-2 font-mono">{r.total_box || "—"}</td>
                  <td className="px-4 py-2 font-mono">{r.dcet_no || "—"}</td>
                  <td className="px-4 py-2">{r.pt_file_load_by_shopper || "—"}</td>
                  <td className="px-4 py-2">{r.pt_file_load_in_qb || "—"}</td>
                  <td className="px-4 py-2">{r.transporter_name}</td>
                  <td className="px-4 py-2">{r.material_receiver_name}</td>
                  <td className="px-4 py-2 text-xs">{nameFor(r.entered_by)}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{new Date(r.created_at).toLocaleString()}</td>
                  {isAdmin && (
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded-sm"><PencilSimple size={14}/></button>
                        <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash size={14}/></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length===0 && <tr><td colSpan={isAdmin?18:17} className="px-4 py-10 text-center text-slate-500">No purchases</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-3xl">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Purchase</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-2">
            {F("Purchase Order No", "purchase_order_no")}
            {F("Purchase Order Date", "purchase_order_date", "date")}
            {F("Reg Serial No", "reg_serial_no")}
            {F("Reg Date", "reg_date", "date")}
            {F("Supplier Name", "supplier_name")}
            {F("Invoice Number", "invoice_number")}
            {F("Invoice Date", "invoice_date", "date")}
            {F("Total Quantity", "total_quantity", "number")}
            {F("Total Shipper", "total_shipper", "number")}
            {F("Total Piece", "total_piece", "number")}
            {F("Total Box", "total_box", "number")}
            {F("DCET No", "dcet_no")}
            {F("PT File Load By / SHOPPER", "pt_file_load_by_shopper")}
            {F("PT File Load in QB", "pt_file_load_in_qb")}
            {F("Transporter Name", "transporter_name")}
            {F("Transporter Builty No", "transporter_builty_no")}
            {F("Material Receiver Name", "material_receiver_name")}
            <div className="col-span-2"><label className="label-cap">Remark (optional)</label><Textarea value={form.remark} onChange={(e)=>setForm({...form, remark: e.target.value})} rows={2}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="purchase-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
